import * as React from "react"
import {
  DayButton as DefaultDayButton,
  DayPicker,
  type CustomComponents,
  type DateRange,
  type Modifiers,
} from "react-day-picker"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

type RangeSelectHandler = (
  range: DateRange | undefined,
  day: Date,
  modifiers: Modifiers,
  event: React.MouseEvent,
) => void

function orderRange(a: Date, b: Date): DateRange {
  return a.getTime() <= b.getTime() ? { from: a, to: b } : { from: b, to: a }
}

/**
 * Calendar — design-system wrapper around `react-day-picker` (v10).
 *
 * Fully styled via `classNames` against Nexus tokens (no upstream CSS import),
 * so it inherits radius, color, and spacing from the design system. Supports
 * single / multiple / range selection through the underlying DayPicker props.
 */
const CHEVRON_BY_ORIENTATION = {
  left: "chevron-left",
  right: "chevron-right",
  up: "chevron-up",
  down: "chevron-down",
} as const

function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  captionLayout = "label",
  ...props
}: CalendarProps) {
  const isRange = props.mode === "range"
  const draggingRef = React.useRef(false)
  const anchorRef = React.useRef<Date | null>(null)

  React.useEffect(() => {
    if (!isRange) return
    const stopDragging = () => {
      draggingRef.current = false
      anchorRef.current = null
    }
    window.addEventListener("pointerup", stopDragging)
    return () => window.removeEventListener("pointerup", stopDragging)
  }, [isRange])

  // Only range mode exposes a DateRange `onSelect`; narrow it for drag updates.
  const rangeOnSelect = isRange ? (props.onSelect as RangeSelectHandler | undefined) : undefined

  const dragComponents: Partial<CustomComponents> | undefined = isRange
    ? {
        DayButton: (dayButtonProps: React.ComponentProps<typeof DefaultDayButton>) => (
          <DefaultDayButton
            {...dayButtonProps}
            onPointerDown={(event) => {
              draggingRef.current = true
              anchorRef.current = dayButtonProps.day.date
              dayButtonProps.onPointerDown?.(event)
            }}
          />
        ),
      }
    : undefined

  return (
    <DayPicker
      {...props}
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn("p-3 select-none", className)}
      onDayMouseEnter={
        isRange
          ? (day, modifiers, event) => {
              if (draggingRef.current && anchorRef.current && event.buttons === 1) {
                rangeOnSelect?.(orderRange(anchorRef.current, day), day, modifiers, event)
              }
            }
          : props.onDayMouseEnter
      }
      classNames={{
        months: "relative flex flex-col gap-4 sm:flex-row sm:gap-5",
        month: "flex flex-col gap-2.5",
        month_caption: "flex h-7 items-center justify-center px-8",
        caption_label: cn(
          "inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[0.8rem] font-medium text-foreground",
          "[&>svg]:size-3.5 [&>svg]:text-muted-foreground",
        ),
        dropdowns: "flex items-center justify-center gap-1",
        dropdown_root:
          "relative inline-flex items-center rounded-sm transition-colors hover:bg-muted focus-within:ring-2 focus-within:ring-ring",
        dropdown: "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0",
        nav: "absolute inset-x-0 top-0 z-10 flex items-center justify-between",
        button_previous: cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-sm border border-input-border bg-background text-foreground transition-colors",
          "hover:bg-muted disabled:pointer-events-none disabled:opacity-40",
        ),
        button_next: cn(
          "inline-flex h-7 w-7 items-center justify-center rounded-sm border border-input-border bg-background text-foreground transition-colors",
          "hover:bg-muted disabled:pointer-events-none disabled:opacity-40",
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "w-8 pb-1 text-[0.68rem] font-normal text-muted-foreground",
        week: "mt-0.5 flex w-full",
        day: cn(
          "relative h-8 w-8 p-0 text-center text-[0.8rem]",
          "focus-within:relative focus-within:z-20",
          // Range lozenge background lives on the cell so it connects edge-to-edge.
          "[&.range-middle]:bg-primary-subtle [&.range-middle]:[&>button]:text-primary-subtle-foreground",
          "[&.range-start]:rounded-l-md [&.range-start]:bg-primary-subtle",
          "[&.range-end]:rounded-r-md [&.range-end]:bg-primary-subtle",
          // Outside days mirror adjacent months — keep them muted, not selected.
          "[&.outside.range-middle]:bg-transparent [&.outside.range-start]:bg-transparent [&.outside.range-end]:bg-transparent",
          "[&.outside.range-middle]:[&>button]:text-muted-foreground/40 [&.outside.range-start]:[&>button]:text-muted-foreground/40 [&.outside.range-end]:[&>button]:text-muted-foreground/40",
        ),
        day_button: cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-sm font-normal text-foreground outline-none",
          "transition-[color,background-color,transform] duration-150 ease-out",
          "hover:bg-accent hover:text-accent-foreground active:scale-90",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
        ),
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary-hover [&>button]:hover:text-primary-foreground [&.outside]:[&>button]:bg-transparent [&.outside]:[&>button]:text-muted-foreground/40 [&.outside]:[&>button]:hover:bg-transparent [&.outside]:[&>button]:hover:text-muted-foreground/40",
        range_start: "range-start",
        range_middle: "range-middle [&>button]:bg-transparent [&>button]:hover:bg-primary/10 rounded-none",
        range_end: "range-end",
        today: "[&>button]:font-semibold [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-primary/40",
        outside: "[&>button]:text-muted-foreground/40",
        disabled: "[&>button]:text-muted-foreground/30",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) => (
          <Icon
            name={CHEVRON_BY_ORIENTATION[orientation ?? "right"]}
            size="sm"
            className={cn("text-foreground", chevronClassName)}
          />
        ),
        ...dragComponents,
        ...components,
      }}
    />
  )
}

export { Calendar }
